import { supabase } from '../supabase';
import { dedupeRequest, getCacheKey } from '../utils/queryDeduplication';

/**
 * Unified Search Service
 * Provides search functionality across protocols, posts/biohacks, and users
 */
class SearchService {
  /**
   * Debounce helper to prevent excessive API calls
   */
  static debounceTimers = {};

  /**
   * Sanitize search term for use inside PostgREST .or() filter string.
   * Removes characters that could break the or() syntax and cause filter injection: ), (, ,
   */
  static sanitizeForOrFilter(s) {
    if (typeof s !== 'string') return '';
    return s.replace(/[(),]/g, '').trim();
  }

  /**
   * Active search abort controller to cancel in-flight requests
   */
  static abortController = null;

  /**
   * Search across all content types
   * Uses request deduplication to prevent duplicate requests
   * @param {string} query - Search query string
   * @param {number} limit - Max results per type (default: 5)
   * @returns {Promise<Object>} Object with protocols, posts, biohacks, and users arrays
   */
  static async searchAll(query, limit = 5) {
    if (!query || query.trim().length === 0) {
      return {
        protocols: [],
        posts: [],
        biohacks: [],
        users: [],
        totalResults: 0
      };
    }

    const normalizedQuery = query.trim().toLowerCase();
    const cacheKey = getCacheKey('searchAll', [normalizedQuery, limit]);
    
    return dedupeRequest(cacheKey, async () => {
      // Cancel previous request if still in flight
      if (this.abortController) {
        this.abortController.abort();
      }

      // Create new abort controller
      this.abortController = new AbortController();

      try {
        // Execute all searches in parallel
        const [protocols, posts, biohacks, users] = await Promise.all([
          this.searchProtocols(normalizedQuery, limit),
          this.searchPosts(normalizedQuery, limit),
          this.searchBiohacks(normalizedQuery, limit),
          this.searchUsers(normalizedQuery, limit)
        ]);

        const totalResults = protocols.length + posts.length + biohacks.length + users.length;

        return {
          protocols,
          posts,
          biohacks,
          users,
          totalResults,
          query
        };
      } catch (error) {
        if (error.name === 'AbortError') {
          console.log('Search cancelled');
          return null;
        }
        console.error('Error in searchAll:', error);
        throw error;
      }
    });
  }

  /**
   * Search protocols by name and description
   * Uses request deduplication to prevent duplicate requests
   */
  static async searchProtocols(query, limit = 5) {
    if (!query || query.trim().length === 0) {
      return [];
    }

    const normalizedQuery = query.trim().toLowerCase();
    const cacheKey = getCacheKey('searchProtocols', [normalizedQuery, limit]);
    const safeForOr = SearchService.sanitizeForOrFilter(normalizedQuery);
    if (!safeForOr) return [];

    return dedupeRequest(cacheKey, async () => {
      try {
        // First get protocols, then fetch creator info separately if needed
        const { data, error } = await supabase
          .from('protocols')
          .select('id, name, description, created_by, created_at')
          .or(`name.ilike.%${safeForOr}%,description.ilike.%${safeForOr}%`)
          .order('created_at', { ascending: false })
          .limit(limit);

        if (error) {
          console.error('Error in searchProtocols query:', error);
          throw error;
        }

        console.log('Protocol search results for query:', normalizedQuery, data);

        // Get creator usernames
        const protocolsWithCreators = await Promise.all(
          (data || []).map(async (protocol) => {
            let creator = 'Unknown';
            let creatorId = null;
            if (protocol.created_by) {
              const { data: userData } = await supabase
                .from('user_profiles')
                .select('username')
                .eq('user_id', protocol.created_by)
                .maybeSingle();
              creator = userData?.username || 'Unknown';
              creatorId = protocol.created_by;
            }
            
            return {
              id: protocol.id,
              name: protocol.name,
              description: protocol.description,
              creator,
              creatorId,
              type: 'protocol',
              createdAt: protocol.created_at
            };
          })
        );

        return protocolsWithCreators;
      } catch (error) {
        console.error('Error searching protocols:', error);
        return [];
      }
    });
  }

  /**
   * Search posts by content
   * Uses request deduplication to prevent duplicate requests
   */
  static async searchPosts(query, limit = 5) {
    if (!query || query.trim().length === 0) {
      return [];
    }

    const normalizedQuery = query.trim().toLowerCase();
    const cacheKey = getCacheKey('searchPosts', [normalizedQuery, limit]);
    
    return dedupeRequest(cacheKey, async () => {
      try {
        const { data, error } = await supabase
          .from('posts')
          .select(`
            id,
            content,
            created_at,
            user_id,
            likes_count,
            user_profiles!posts_user_id_fkey(username, profile_picture_url)
          `)
          .ilike('content', `%${normalizedQuery}%`)
          .lte('created_at', new Date().toISOString())
          .order('created_at', { ascending: false })
          .limit(limit);

        if (error) {
          console.error('Error in searchPosts query:', error);
          throw error;
        }

        console.log('Post search results for query:', normalizedQuery, data);

        return (data || []).map(post => ({
          id: post.id,
          content: post.content,
          username: post.user_profiles?.username || 'Unknown',
          avatar: post.user_profiles?.profile_picture_url,
          userId: post.user_id,
          likesCount: post.likes_count || 0,
          type: 'post',
          createdAt: post.created_at
        }));
      } catch (error) {
        console.error('Error searching posts:', error);
        return [];
      }
    });
  }

  /**
   * Search biohacks by name and description
   * Uses request deduplication to prevent duplicate requests
   */
  static async searchBiohacks(query, limit = 5) {
    if (!query || query.trim().length === 0) {
      return [];
    }

    const normalizedQuery = query.trim().toLowerCase();
    const cacheKey = getCacheKey('searchBiohacks', [normalizedQuery, limit]);
    const safeForOr = SearchService.sanitizeForOrFilter(normalizedQuery);
    if (!safeForOr) return [];

    return dedupeRequest(cacheKey, async () => {
      try {
        const { data, error } = await supabase
          .from('biohacks')
          .select(`
            id,
            name,
            description,
            image_url,
            category
          `)
          .or(`name.ilike.%${safeForOr}%,description.ilike.%${safeForOr}%`)
          .order('name', { ascending: true })
          .limit(limit);

        if (error) {
          console.error('Error in searchBiohacks query:', error);
          throw error;
        }

        console.log('Biohack search results for query:', normalizedQuery, data);

        return (data || []).map(biohack => ({
          id: biohack.id,
          name: biohack.name,
          description: biohack.description,
          image: biohack.image_url,
          category: biohack.category,
          type: 'biohack'
        }));
      } catch (error) {
        console.error('Error searching biohacks:', error);
        return [];
      }
    });
  }

  /**
   * Search biohacks by name only (no description)
   * Uses request deduplication to prevent duplicate requests
   */
  static async searchBiohacksByName(query, limit = 5) {
    if (!query || query.trim().length === 0) {
      return [];
    }

    const normalizedQuery = query.trim().toLowerCase();
    const cacheKey = getCacheKey('searchBiohacksByName', [normalizedQuery, limit]);
    
    return dedupeRequest(cacheKey, async () => {
      try {
        const { data, error } = await supabase
          .from('biohacks')
          .select(`
            id,
            name,
            image_url,
            category
          `)
          .ilike('name', `%${normalizedQuery}%`)
          .order('name', { ascending: true })
          .limit(limit);

        if (error) {
          console.error('Error in searchBiohacksByName query:', error);
          throw error;
        }

        return (data || []).map(biohack => ({
          id: biohack.id,
          name: biohack.name,
          image: biohack.image_url,
          category: biohack.category,
          type: 'biohack'
        }));
      } catch (error) {
        console.error('Error searching biohacks by name:', error);
        return [];
      }
    });
  }

  /**
   * Search biomarkers by name only
   * Uses request deduplication to prevent duplicate requests
   */
  static async searchBiomarkersByName(query, limit = 5) {
    if (!query || query.trim().length === 0) {
      return [];
    }

    const normalizedQuery = query.trim().toLowerCase();
    const cacheKey = getCacheKey('searchBiomarkersByName', [normalizedQuery, limit]);
    
    return dedupeRequest(cacheKey, async () => {
      try {
        const { data, error } = await supabase
          .from('biomarkers')
          .select(`
            id,
            name,
            category,
            unit
          `)
          .ilike('name', `%${normalizedQuery}%`)
          .order('name', { ascending: true })
          .limit(limit);

        if (error) {
          console.error('Error in searchBiomarkersByName query:', error);
          throw error;
        }

        return (data || []).map(biomarker => ({
          id: biomarker.id,
          name: biomarker.name,
          category: biomarker.category,
          unit: biomarker.unit,
          type: 'biomarker'
        }));
      } catch (error) {
        console.error('Error searching biomarkers by name:', error);
        return [];
      }
    });
  }

  /**
   * Search users by username
   * Uses request deduplication to prevent duplicate requests
   */
  static async searchUsers(query, limit = 5) {
    if (!query || query.trim().length === 0) {
      return [];
    }

    const normalizedQuery = query.trim().toLowerCase();
    const cacheKey = getCacheKey('searchUsers', [normalizedQuery, limit]);
    
    return dedupeRequest(cacheKey, async () => {
      try {
        // Query from user_profiles table directly
        const { data, error } = await supabase
          .from('user_profiles')
          .select('user_id, username, profile_picture_url')
          .ilike('username', `%${normalizedQuery}%`)
          .limit(limit);

        if (error) {
          console.error('Error in searchUsers query:', error);
          throw error;
        }

        console.log('Search results for query:', normalizedQuery, data);

        return (data || []).map(profile => ({
          id: profile.user_id,
          username: profile.username || 'Unknown',
          avatar: profile.profile_picture_url,
          type: 'user'
        }));
      } catch (error) {
        console.error('Error searching users:', error);
        return [];
      }
    });
  }

  /**
   * Track search analytics
   */
  static trackSearchEvent(eventName, data) {
    try {
      // Implement analytics tracking here
      // For now, just log to console
      console.log(`[Analytics] ${eventName}:`, data);
      
      // You can integrate with your analytics service here
      // e.g., window.gtag, window.analytics, etc.
    } catch (error) {
      console.error('Error tracking search event:', error);
    }
  }
}

export default SearchService;

